import { normalizeColor, normalizeComment, normalizeName } from './text';
import type { KinklistDoc } from './types';
import { LIMITS } from './types';

/**
 * Every rule a valid document satisfies (SPEC §4). Returns human-readable
 * violations; an empty array means the document is valid. Shared by the zod
 * schema and the property tests for ops.
 */
export function findInvariantViolations(doc: KinklistDoc): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  const claim = (id: string, what: string) => {
    if (seen.has(id)) problems.push(`duplicate id ${id} (${what})`);
    seen.add(id);
  };
  const checkName = (name: string, what: string) => {
    if (normalizeName(name) !== name)
      problems.push(`invalid name for ${what}: ${JSON.stringify(name)}`);
  };

  if (doc.title.length > LIMITS.title) problems.push('title too long');
  if (doc.ratings.length < LIMITS.minRatings) problems.push('fewer than 2 ratings');

  const ratingIds = new Set<string>();
  for (const rating of doc.ratings) {
    claim(rating.id, `rating ${rating.name}`);
    ratingIds.add(rating.id);
    checkName(rating.name, `rating ${rating.id}`);
    if (normalizeColor(rating.color) !== rating.color) {
      problems.push(`invalid color for rating ${rating.id}: ${rating.color}`);
    }
  }

  for (const category of doc.categories) {
    claim(category.id, `category ${category.name}`);
    checkName(category.name, `category ${category.id}`);
    if (category.roles.length < LIMITS.minRoles || category.roles.length > LIMITS.maxRoles) {
      problems.push(`category ${category.id} has ${String(category.roles.length)} roles`);
    }
    for (const role of category.roles) {
      claim(role.id, `role ${role.name}`);
      checkName(role.name, `role ${role.id}`);
    }
    const roleIds = category.roles.map((role) => role.id);
    for (const kink of category.kinks) {
      claim(kink.id, `kink ${kink.name}`);
      checkName(kink.name, `kink ${kink.id}`);
      if (kink.comment !== undefined && normalizeComment(kink.comment) !== kink.comment) {
        problems.push(`invalid comment on kink ${kink.id}`);
      }
      const keys = Object.keys(kink.ratings);
      if (
        keys.length !== roleIds.length ||
        !roleIds.every((id) => Object.hasOwn(kink.ratings, id))
      ) {
        problems.push(`kink ${kink.id} ratings don't match the roles of category ${category.id}`);
      }
      for (const ratingId of Object.values(kink.ratings)) {
        if (!ratingIds.has(ratingId))
          problems.push(`kink ${kink.id} uses unknown rating ${ratingId}`);
      }
    }
  }
  return problems;
}
